import { Nullable } from '@ui/types/utility';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
} from 'react';

const historyCardContext = createContext({
  isQueryEnabled: false,
});

export const HistoryCardContextProvider = ({
  children,
  isQueryEnabled,
}: {
  children: ReactNode;
  isQueryEnabled: boolean;
}) => {
  return (
    <historyCardContext.Provider
      value={useMemo(
        () => ({
          isQueryEnabled: isQueryEnabled,
        }),
        [isQueryEnabled],
      )}
    >
      {children}
    </historyCardContext.Provider>
  );
};

export const useHistoryCardContext = () => useContext(historyCardContext);

/**
 * Class applied to each row's left stripe so the injected hover CSS can target
 * it. Kept in sync with the rule strings emitted below.
 */
export const HISTORY_STRIPE_CLASS = 'hist-stripe';

const SCOPE_ATTR = 'data-hist-scope';
const GROUP_ATTR = 'data-group';

/**
 * Hover grouping is done entirely through CSS, not React state: hovering a row
 * imperatively rewrites a scoped <style> tag so the same-group rows light up
 * and others dim. This keeps hover at zero re-renders even on long lists, and
 * lazily-loaded rows are picked up automatically by the selectors.
 */
const historyHoverContext = createContext<{
  scopeId: string;
  setHoveredGroup: (hash: Nullable<string>) => void;
}>({
  scopeId: '',
  setHoveredGroup: () => {},
});

export const HistoryHoverProvider = ({ children }: { children: ReactNode }) => {
  const rawId = useId();
  // useId contains ':' which is illegal in attribute selectors — sanitize.
  const scopeId = useMemo(
    () => `hist${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`,
    [rawId],
  );
  const styleRef = useRef<HTMLStyleElement>(null);

  const setHoveredGroup = useCallback(
    (hash: Nullable<string>) => {
      const el = styleRef.current;
      if (!el) return;

      if (!hash) {
        el.textContent = '';

        return;
      }

      const scope = `[${SCOPE_ATTR}="${scopeId}"]`;
      // positionHash is hex(+0x); strip anything that could break the selector.
      const group = hash.replace(/[^a-zA-Z0-9_-]/g, '');

      el.textContent =
        // whisper-level group tint (Ostium-calibrated); the direct row's own
        // hover (3.5% white) stays the stronger cue
        `${scope}[${GROUP_ATTR}="${group}"]{background-color:rgba(255,255,255,0.02);}` +
        `${scope}[${GROUP_ATTR}="${group}"] .${HISTORY_STRIPE_CLASS}{opacity:1 !important;width:2px !important;}` +
        `${scope}:not([${GROUP_ATTR}="${group}"]) .${HISTORY_STRIPE_CLASS}{opacity:0.12 !important;}`;
    },
    [scopeId],
  );

  return (
    <historyHoverContext.Provider
      value={useMemo(
        () => ({ scopeId, setHoveredGroup }),
        [scopeId, setHoveredGroup],
      )}
    >
      {/* content set imperatively; React keeps hands off (no children) */}
      <style ref={styleRef} />
      {children}
    </historyHoverContext.Provider>
  );
};

export const useHistoryHover = () => useContext(historyHoverContext);
export const HISTORY_SCOPE_ATTR = SCOPE_ATTR;
export const HISTORY_GROUP_ATTR = GROUP_ATTR;
